'use strict';

import { loadLocale } from './components/datepicker-locales';

// Trigger registry, the only place that adds component JS. When an element matching a selector is in the
// page, initially or added later, its module is downloaded and run once; an exported init(element) then
// runs once per matching element.
const TRIGGERS = [
    {
        selector: '.display-checkin, .display-checkout, #month-range',
        // The translation downloads together with the calendar.
        load: () => {
            loadLocale();
            return import(/* webpackChunkName: "datepicker" */ './modules/datepicker');
        },
    },
    {
        selector: 'select[data-select]',
        load: () => import(/* webpackChunkName: "select" */ './modules/select'),
    },
    // Bootstrap's Tooltip with Popper.
    {
        selector: '[data-bs-toggle="tooltip"], [data-toggle="tooltip"]',
        load: () => import(/* webpackChunkName: "tooltip" */ './modules/tooltip'),
    },
    // Bootstrap's Tab and Carousel: their data attributes work once the component is loaded.
    {
        selector: '[data-bs-toggle="tab"], [data-bs-toggle="pill"], [data-bs-toggle="list"]',
        load: () => import(/* webpackChunkName: "tab" */ 'bootstrap/js/src/tab'),
    },
    {
        selector: '.carousel',
        load: () => import(/* webpackChunkName: "carousel" */ 'bootstrap/js/src/carousel'),
    },
    {
        selector: '[hx-get],[hx-post],[hx-put],[hx-patch],[hx-delete]',
        load: () => import(/* webpackChunkName: "htmx" */ './modules/htmx'),
    },
    // The reservation and edit forms share one file of modules.
    {
        selector: '[data-component="booking"]',
        load: () => import(/* webpackChunkName: "booking" */ './modules/booking'),
    },
    {
        selector: '[data-component="cars"]',
        load: () => import(/* webpackChunkName: "booking" */ './modules/cars'),
    },
    {
        selector: '[data-component="numeric-buttons"]',
        load: () => import(/* webpackChunkName: "booking" */ './modules/numeric-input'),
    },
    {
        selector: 'textarea[data-char-counter]',
        load: () => import(/* webpackChunkName: "booking" */ './modules/char-counter'),
    },
    {
        selector: '#reservation-config',
        load: () => import(/* webpackChunkName: "booking" */ './modules/reservation-form'),
    },
    {
        selector: '.insurance',
        load: () => import(/* webpackChunkName: "insurance" */ './modules/insurance'),
    },
    // The listing page: sorting, filters, offers and price fitting share one file.
    {
        selector: '#offers',
        load: () => import(/* webpackChunkName: "listing" */ './modules/listing'),
    },
    {
        selector: '.offer__clickable, .offer__clickable__opinions, .offer__clickable__gallery, .search_param',
        load: () => import(/* webpackChunkName: "listing" */ './modules/offers'),
    },
    {
        selector: '.offer__price',
        load: () => import(/* webpackChunkName: "listing" */ './modules/price-fit'),
    },
];

// Content rendered inside these widgets is never a trigger, so changes there are not inspected.
const WIDGETS = '.datepicker, .ts-wrapper, .ts-dropdown';

const ANY_TRIGGER = TRIGGERS.map((trigger) => trigger.selector).join(',');

for (const trigger of TRIGGERS) trigger.done = new WeakSet();

function find(root) {
    const found = [...root.querySelectorAll(ANY_TRIGGER)];
    if (root.matches(ANY_TRIGGER)) found.push(root);
    return found;
}

function activate(elements) {
    const loading = [];
    for (const trigger of TRIGGERS) {
        const matched = [];
        for (const element of elements) {
            // Marked before init, so DOM changes made by init do not trigger it again.
            if (!trigger.done.has(element) && element.matches(trigger.selector)) {
                trigger.done.add(element);
                matched.push(element);
            }
        }
        if (!matched.length) continue;
        if (!trigger.module) {
            trigger.module = trigger.load().catch((error) => {
                // An element matched later downloads the module again.
                trigger.module = null;
                throw error;
            });
        }
        loading.push(
            trigger.module
                .then((loaded) => loaded.init && matched.forEach((element) => loaded.init(element)))
                // Reported as an uncaught error without holding back other modules or window.parklot.ready.
                .catch((error) => setTimeout(() => { throw error; })),
        );
    }
    return Promise.all(loading);
}

// Initialises the triggers in the page, then watches for added content. Resolves once the modules
// triggered by the page are initialised.
export function watchTriggers() {
    const observer = new MutationObserver((records) => {
        const added = [];
        for (const record of records) {
            if (record.target.closest(WIDGETS)) continue;
            for (const node of record.addedNodes) {
                if (node.nodeType === Node.ELEMENT_NODE) added.push(...find(node));
            }
        }
        if (added.length) activate(added);
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return activate(find(document.body));
}
